import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import api from '../services/api';

const CATEGORIAS_PREDEFINIDAS = [
    'Soporte', 'Procedimientos', 'Redes', 'Software', 'Hardware',
    'Sistemas', 'Seguridad', 'Políticas', 'Herramientas', 'Aplicaciones', 'Proveedores'
];

export default function EditorArticulo() {
    const { id } = useParams();
    const navigate = useNavigate();
    const editMode = Boolean(id);
    const tinyApiKey = process.env.REACT_APP_TINYMCE_KEY || '';

    const [loading, setLoading] = useState(false);
    const [topics, setTopics] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [form, setForm] = useState({
        titulo: '',
        descripcion: '',
        contenido: '',
        categoria: '',
        esPublico: false,
        tags: '',
        topicId: ''
    });
    const [archivos, setArchivos] = useState([]);
    const [imageUrlMap, setImageUrlMap] = useState({}); // blobUrl -> secureUrl
    const [imagesHydrated, setImagesHydrated] = useState(false);
    const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
    const [showNewCat, setShowNewCat] = useState(false);
    const [newCatName, setNewCatName] = useState('');

    useEffect(() => {
        const init = async () => {
            try {
                const [t, c] = await Promise.all([
                    api.get('/baseconocimiento/topics'),
                    api.get('/baseconocimiento/categorias').catch(() => ({ data: [] }))
                ]);
                setTopics(t.data || []);
                // Combinar categorías predefinidas con las del backend (deduplicar y ordenar)
                const backendCats = c.data || [];
                const todas = [...new Set([...CATEGORIAS_PREDEFINIDAS, ...backendCats])].sort();
                setCategorias(todas);
            } catch {
                setCategorias(CATEGORIAS_PREDEFINIDAS);
            }
            // Si viene topicId por query, preseleccionarlo en modo creación
            if (!editMode) {
                try {
                    const params = new URLSearchParams(window.location.search);
                    const tid = params.get('topicId');
                    if (tid) setForm(prev => ({ ...prev, topicId: tid }));
                } catch { }
            }
            if (editMode) {
                setLoading(true);
                try {
                    const res = await api.get(`/baseconocimiento/${id}`);
                    const a = res.data;
                    setForm({
                        titulo: a.titulo || '',
                        descripcion: a.descripcion || '',
                        contenido: a.contenido || '',
                        categoria: a.categoria || '',
                        esPublico: !!a.esPublico,
                        tags: a.tags || '',
                        topicId: (a.topic && a.topic.id) || a.topicId || ''
                    });
                    try {
                        const ar = await api.get(`/baseconocimiento/${id}/archivos`);
                        setArchivos(ar.data || []);
                    } catch { }
                } finally {
                    setLoading(false);
                }
            }
        };
        init();
        // Detectar cambios de tema (Tailwind dark class)
        const observer = new MutationObserver(() => {
            setIsDark(document.documentElement.classList.contains('dark'));
        });
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
        return () => observer.disconnect();
    }, [id, editMode]);

    // Hidratar imágenes existentes con URLs seguras o firmadas para que se vean en el editor (convertir a blob autenticado)
    useEffect(() => {
        const hydrateImages = async () => {
            if (!editMode || imagesHydrated) return;
            const html = form.contenido || '';
            // Extraer tanto preview permanentes como kb-signed
            const previewRegex = /(https?:\/\/[^\s"']+\/securefile\/preview\/baseconocimiento\/[^\s"']+)/g;
            const signedRegex = /(https?:\/\/[^\s"']+\/securefile\/kb-signed\?[^\s"']*f=([^&"']+)[^\s"']*)/g;
            const previewUrls = Array.from(new Set(Array.from(html.matchAll(previewRegex)).map(m => m[1])));
            const signedMatches = Array.from(html.matchAll(signedRegex));
            const signedToPreview = signedMatches.map((m) => {
                try {
                    const full = m[1];
                    const fileName = decodeURIComponent(m[2]);
                    const u = new URL(full, window.location.origin);
                    return `${u.origin}/api/securefile/preview/baseconocimiento/${fileName}`;
                } catch { return null; }
            }).filter(Boolean);
            const urls = Array.from(new Set([...previewUrls, ...signedToPreview]));
            if (urls.length === 0) { setImagesHydrated(true); return; }
            try {
                const { createKbObjectUrl } = await import('../utils/knowledgeFileProxy');
                let newHtml = html;
                const newMap = {};
                for (const url of urls) {
                    try {
                        const blobUrl = await createKbObjectUrl(url);
                        newHtml = newHtml.split(url).join(blobUrl);
                        newMap[blobUrl] = url;
                    } catch (e) {
                        // si falla alguna, continuamos
                    }
                }
                setImageUrlMap(prev => ({ ...prev, ...newMap }));
                setForm(prev => ({ ...prev, contenido: newHtml }));
            } finally {
                setImagesHydrated(true);
            }
        };
        hydrateImages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editMode, form.contenido]);

    const handleGuardar = async () => {
        setLoading(true);
        try {
            // 1) Subir imágenes incrustadas como data: si existen (p.ej. pegadas)
            if (editMode && form.contenido && form.contenido.includes('src="data:')) {
                let html = form.contenido;
                const doc = new DOMParser().parseFromString(html, 'text/html');
                const imgs = Array.from(doc.querySelectorAll('img'));
                for (const img of imgs) {
                    const src = img.getAttribute('src') || '';
                    if (!src.startsWith('data:')) continue;
                    try {
                        const res = await fetch(src);
                        const blob = await res.blob();
                        const file = new File([blob], 'pasted.png', { type: blob.type || 'image/png' });
                        const fd = new FormData();
                        fd.append('archivos', file, file.name);
                        const up = await api.post(`/baseconocimiento/${id}/archivos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                        const url = up.data?.archivos?.[0]?.url;
                        if (url) {
                            // Para que se vea en el editor ahora mismo, convierto a blob auth
                            try {
                                const { createKbObjectUrl } = await import('../utils/knowledgeFileProxy');
                                const blobUrl = await createKbObjectUrl(url);
                                setImageUrlMap(prev => ({ ...prev, [blobUrl]: url }));
                                img.setAttribute('src', blobUrl);
                            } catch {
                                img.setAttribute('src', url);
                            }
                        }
                    } catch { }
                }
                html = doc.body.innerHTML;
                // actualizar form para que el siguiente paso reemplace blobs->secure
                setForm(prev => ({ ...prev, contenido: html }));
            }

            // Reemplazar cualquier blob: por su URL segura antes de guardar
            let contenidoToSave = form.contenido;
            Object.entries(imageUrlMap).forEach(([blobUrl, secureUrl]) => {
                if (blobUrl && secureUrl && contenidoToSave.includes(blobUrl)) {
                    contenidoToSave = contenidoToSave.split(blobUrl).join(secureUrl);
                }
            });

            // Normalizar y evitar guardar URLs firmadas con expiración
            // Ej: /api/securefile/kb-signed?f=nombre.png&e=...&s=...
            // Consumir toda la query del kb-signed para que no queden restos (&e=...)
            contenidoToSave = contenidoToSave.replace(/\b(https?:\/\/[^\s"']+)?\/api?\/securefile\/kb-signed\?[^\s"']*?f=([^&"']+)[^\s"']*/g,
                (match, host, fileNameEnc) => {
                    try {
                        const fileName = decodeURIComponent(fileNameEnc);
                        const base = host ? host.replace(/\/$/, '') : '';
                        return `${base}/api/securefile/preview/baseconocimiento/${fileName}`;
                    } catch {
                        return match;
                    }
                }
            );

            const payload = { ...form, contenido: contenidoToSave };
            if (editMode) {
                await api.put(`/baseconocimiento/${id}`, payload);
            } else {
                const res = await api.post('/baseconocimiento', payload);
                if (res.data?.id) navigate(`/base-conocimiento/editar/${res.data.id}`);
            }
            navigate('/admin-base-conocimiento');
        } finally {
            setLoading(false);
        }
    };

    // Nota: módulos de resize de imagen pueden romper con ciertas builds de Quill/CRA; por ahora desactivamos el registro para evitar errores

    const tinyInit = useMemo(() => ({
        height: 600,
        menubar: true,
        // Quitamos 'paste' para evitar cargar plugin desde CDN; usaremos paste_data_images
        plugins: 'advlist autolink lists link image charmap preview anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
        toolbar: 'undo redo | blocks | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image media table | removeformat | code',
        image_title: true,
        automatic_uploads: true,
        // Permitimos pegar imágenes; subiremos a servidor en guardado o al pegar si es posible
        paste_data_images: true,
        images_file_types: 'jpg,jpeg,png,gif,webp',
        file_picker_types: 'image',
        // Adaptar a tema oscuro/claro
        skin: isDark ? 'oxide-dark' : 'oxide',
        content_css: isDark ? 'dark' : '',
        images_upload_handler: async (blobInfo) => {
            // Subida básica a adjuntos del artículo en edición (si existe id); si no, usar base64 temporal
            try {
                const formData = new FormData();
                formData.append('archivos', blobInfo.blob(), blobInfo.filename());
                const uploadId = id;
                if (!uploadId) {
                    // si no hay id aún, retornar base64 temporal
                    return `data:${blobInfo.blob().type};base64,${blobInfo.base64()}`;
                }
                const up = await api.post(`/baseconocimiento/${uploadId}/archivos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                const url = up.data?.archivos?.[0]?.url;
                const signed = up.data?.archivos?.[0]?.signedUrl;
                if (url) {
                    // Para previsualizar en el editor (iframe sin auth), obtener blob local autenticado
                    const { createKbObjectUrl } = await import('../utils/knowledgeFileProxy');
                    try {
                        const blobUrl = signed ? signed : await createKbObjectUrl(url);
                        setImageUrlMap(prev => ({ ...prev, [blobUrl]: url }));
                        return blobUrl;
                    } catch {
                        return signed || url; // fallback
                    }
                }
                return `data:${blobInfo.blob().type};base64,${blobInfo.base64()}`;
            } catch {
                return `data:${blobInfo.blob().type};base64,${blobInfo.base64()}`;
            }
        },
        image_dimensions: true,
        image_caption: true,
        content_style: `
          :root { color-scheme: ${isDark ? 'dark' : 'light'}; }
          html, body { background: ${isDark ? '#0f172a' : '#ffffff'}; color: ${isDark ? '#e2e8f0' : '#111827'}; font-family: Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif; font-size:14px; }
          a { color: ${isDark ? '#93c5fd' : '#1d4ed8'}; }
          img { max-width:100%; height:auto; border-radius:4px; }
          table{ width:100%; border-collapse:collapse; }
          th,td{ border:1px solid ${isDark ? 'rgba(148,163,184,.25)' : 'rgba(0,0,0,.1)'}; padding:8px; }
          pre, code { background: ${isDark ? '#0b1220' : '#f3f4f6'}; color: inherit; }
        `
    }), [id, isDark]);

    return (
        <div className="p-6">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{editMode ? 'Editar' : 'Nuevo'} artículo</h1>
                <div className="space-x-2">
                    <button onClick={() => navigate(-1)} className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">Cancelar</button>
                    <button onClick={handleGuardar} className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Guardar</button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-4">
                    <input
                        type="text"
                        placeholder="Título"
                        value={form.titulo}
                        onChange={(e) => setForm(prev => ({ ...prev, titulo: e.target.value }))}
                        className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400"
                    />
                    <input
                        type="text"
                        placeholder="Descripción"
                        value={form.descripcion}
                        onChange={(e) => setForm(prev => ({ ...prev, descripcion: e.target.value }))}
                        className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400"
                    />
                    <Editor
                        key={isDark ? 'editor-dark' : 'editor-light'}
                        apiKey={tinyApiKey}
                        value={form.contenido}
                        init={tinyInit}
                        onEditorChange={(content) => setForm(prev => ({ ...prev, contenido: content }))}
                    />
                </div>
                <div className="space-y-4">
                    <select value={form.topicId} onChange={(e) => setForm(prev => ({ ...prev, topicId: e.target.value }))} className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                        <option value="">Seleccionar topic</option>
                        {topics.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
                    </select>
                    <div className="flex gap-2 items-center">
                        <select value={form.categoria} onChange={(e) => {
                            const v = e.target.value; setForm(prev => ({ ...prev, categoria: v }));
                        }} className="flex-1 px-3 py-2 border rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                            <option value="">Categoría</option>
                            {categorias.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button
                            type="button"
                            onClick={() => setShowNewCat(true)}
                            className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-lg font-bold leading-none"
                            title="Agregar nueva categoría"
                        >+</button>
                    </div>
                    {showNewCat && (
                        <div className="flex gap-2 items-center mt-1">
                            <input
                                type="text"
                                placeholder="Nueva categoría..."
                                value={newCatName}
                                onChange={(e) => setNewCatName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && newCatName.trim()) {
                                        const name = newCatName.trim();
                                        if (!categorias.includes(name)) setCategorias(prev => [...prev, name].sort());
                                        setForm(prev => ({ ...prev, categoria: name }));
                                        setNewCatName('');
                                        setShowNewCat(false);
                                    }
                                }}
                                className="flex-1 px-3 py-1.5 border rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 text-sm placeholder-gray-400"
                                autoFocus
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    const name = newCatName.trim();
                                    if (name) {
                                        if (!categorias.includes(name)) setCategorias(prev => [...prev, name].sort());
                                        setForm(prev => ({ ...prev, categoria: name }));
                                    }
                                    setNewCatName('');
                                    setShowNewCat(false);
                                }}
                                className="px-3 py-1.5 bg-green-600 text-white rounded text-sm hover:bg-green-700"
                            >Agregar</button>
                            <button
                                type="button"
                                onClick={() => { setShowNewCat(false); setNewCatName(''); }}
                                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >Cancelar</button>
                        </div>
                    )}

                    <input type="text" placeholder="Tags (separados por comas)" value={form.tags} onChange={(e) => setForm(prev => ({ ...prev, tags: e.target.value }))} className="w-full px-3 py-2 border rounded bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400" />
                    <label className="inline-flex items-center space-x-2">
                        <input type="checkbox" checked={form.esPublico} onChange={(e) => setForm(prev => ({ ...prev, esPublico: e.target.checked }))} />
                        <span>Artículo público</span>
                    </label>

                    {editMode && (
                        <div>
                            <div className="font-semibold mb-2">Adjuntos</div>
                            <input type="file" multiple onChange={async (e) => {
                                const files = Array.from(e.target.files || []);
                                if (files.length === 0) return;
                                const fd = new FormData();
                                files.forEach(f => fd.append('archivos', f));
                                await api.post(`/baseconocimiento/${id}/archivos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                                const ar = await api.get(`/baseconocimiento/${id}/archivos`);
                                setArchivos(ar.data || []);
                            }} />
                            <ul className="mt-2 text-sm divide-y">
                                {archivos.map(a => (
                                    <li key={a.id} className="py-1 flex justify-between">
                                        <a className="text-blue-600" href={a.url} target="_blank" rel="noreferrer">{a.nombre || a.nombreOriginal}</a>
                                        <button className="text-red-600" onClick={async () => {
                                            await api.delete(`/baseconocimiento/archivos/${a.id}`);
                                            const ar = await api.get(`/baseconocimiento/${id}/archivos`);
                                            setArchivos(ar.data || []);
                                        }}>Eliminar</button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}


